import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField
} from "@mui/material";
import { Plus20Px, SquareIconButton } from "@qubit/autoparts";
import { useEffect } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { firstUnusedGridPositionAmongPortsAndTotes } from "~/features/preview/workstation/settingsWorkstation.logic";
import { workstationPreviewPtlCardMaxWidthPx } from "~/features/preview/workstation/workstation.styles";
import { possibleGridCoordinates } from "~/features/preview/workstation/workstationConstants";
import {
  settingsControlFontSize,
  settingsInputMinHeightPx,
  settingsSelectSx
} from "~/features/settings/settings.styles";
import { selectUserIsAdmin } from "~/redux/selectors/authSelectors";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";
import {
  TotePlacementSummaryDto,
  TotePlacementRequestDto,
  UpdateWorkstation,
  WorkstationSummaryDto
} from "~/types/api";

import { PortLabeledRow } from "./PortSetup";

const ptlBoxGapPx = "10px";

const ptlIconButtonSx = {
  width: settingsInputMinHeightPx,
  height: settingsInputMinHeightPx,
  minWidth: settingsInputMinHeightPx,
  minHeight: settingsInputMinHeightPx,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
} as const;

const ptlModuleFieldSx = {
  width: "100%",
  "& .MuiInputBase-input": {
    fontSize: settingsControlFontSize,
    lineHeight: 1.35
  }
} as const;

export function firstUnusedGridCoordinate(
  placements: Pick<TotePlacementSummaryDto, "coordinate">[]
): { x: number; y: number } {
  return firstUnusedGridPositionAmongPortsAndTotes(
    [],
    placements.map((p) => p.coordinate)
  );
}

function positivePutToLightModuleIds(
  placements: Pick<TotePlacementSummaryDto, "putToLightModuleId">[]
): number[] {
  return placements
    .map((tp) => Number(tp.putToLightModuleId))
    .filter((id) => Number.isFinite(id) && id > 0);
}

/**
 * Next default PTL module id when adding a tote placement: max existing id + 1,
 * or 1 if none are set (no id greater than 0). Uses all workstations when the list query has
 * resolved; otherwise only the current form's tote placements.
 */
export function nextDefaultPutToLightModuleId({
  allWorkstations,
  editingWorkstationId,
  formTotePlacements
}: {
  allWorkstations: WorkstationSummaryDto[] | undefined;
  editingWorkstationId: string | undefined;
  formTotePlacements: Pick<TotePlacementSummaryDto, "putToLightModuleId">[];
}): number {
  const ids: number[] = [];

  if (allWorkstations !== undefined) {
    if (editingWorkstationId) {
      for (const ws of allWorkstations) {
        if (ws.id === editingWorkstationId) {
          continue;
        }
        ids.push(...positivePutToLightModuleIds(ws.totePlacements ?? []));
      }
    } else {
      for (const ws of allWorkstations) {
        ids.push(...positivePutToLightModuleIds(ws.totePlacements ?? []));
      }
    }
  }

  ids.push(...positivePutToLightModuleIds(formTotePlacements));

  if (ids.length === 0) {
    return 1;
  }
  return Math.max(...ids) + 1;
}

export function TotePlacementManagement() {
  const { t } = useTranslation();
  const isAdmin = useAppSelector(selectUserIsAdmin);
  const { data: allWorkstations } = useGetWorkstationsQuery();

  const { control, register, getValues, setValue, watch } =
    useFormContext<UpdateWorkstation>();
  const {
    fields: totePlacements,
    append: addTotePlacement,
    remove: removeTotePlacement
  } = useFieldArray({
    control,
    name: "totePlacements"
  });

  const createNewTotePlacementTemplate = (): TotePlacementRequestDto => {
    const ports = getValues("ports") ?? [];
    const portCoordinates = ports.map((p) => p.coordinate);
    const current = getValues("totePlacements") ?? [];
    const { x, y } = firstUnusedGridPositionAmongPortsAndTotes(
      portCoordinates,
      current.map((p) => p.coordinate)
    );
    const putToLightModuleId = nextDefaultPutToLightModuleId({
      allWorkstations: allWorkstations,
      editingWorkstationId: getValues("workstationId"),
      formTotePlacements: current
    });
    return {
      totePosition: current.length + 1,
      putToLightModuleId,
      coordinate: { x, y }
    };
  };

  useEffect(() => {
    const n = totePlacements.length;
    for (let i = 0; i < n; i++) {
      setValue(`totePlacements.${i}.totePosition`, i + 1, {
        shouldDirty: false
      });
    }
  }, [setValue, totePlacements.length]);

  return (
    <Box id="ws-ptl-root">
      <Stack id="ws-ptl-row" direction="row" spacing={1.5} alignItems="stretch">
        <Box
          id="ws-ptl-cards-wrap"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: ptlBoxGapPx,
            flex: "1 1 auto",
            minWidth: 0,
            alignContent: "flex-start"
          }}
          role="list"
        >
          {totePlacements.map((totePlacement, index) => {
            const moduleInputId = `tp-preview-ptl-module-${index}`;
            const xSelectId = `tp-preview-coord-x-${index}`;
            const ySelectId = `tp-preview-coord-y-${index}`;
            return (
              <Paper
                key={totePlacement.id}
                id={`ws-ptl-card-${index}`}
                elevation={0}
                variant="outlined"
                role="listitem"
                sx={{
                  boxSizing: "border-box",
                  minWidth: 0,
                  flex: { xs: "1 1 100%", md: "0 1 auto" },
                  width: { xs: "100%", md: "auto" },
                  maxWidth: {
                    xs: "100%",
                    md: workstationPreviewPtlCardMaxWidthPx
                  },
                  px: 1.5,
                  py: 1.25,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1
                }}
              >
                <Stack
                  id={`ws-ptl-card-${index}-fields`}
                  spacing={1.5}
                  alignItems="stretch"
                  sx={{ width: "100%", minWidth: 0 }}
                >
                  <PortLabeledRow label={t("ptl id")} htmlFor={moduleInputId}>
                    <TextField
                      id={moduleInputId}
                      variant="outlined"
                      size="small"
                      disabled={!isAdmin}
                      fullWidth
                      sx={ptlModuleFieldSx}
                      {...register(
                        `totePlacements.${index}.putToLightModuleId`
                      )}
                    />
                  </PortLabeledRow>
                  <PortLabeledRow
                    label={t("layout coordinate x", {
                      defaultValue: "X"
                    })}
                  >
                    <FormControl fullWidth size="small" disabled={!isAdmin}>
                      <Select
                        id={xSelectId}
                        variant="outlined"
                        disabled={!isAdmin}
                        sx={settingsSelectSx}
                        SelectDisplayProps={{ "aria-label": "X" }}
                        {...register(`totePlacements.${index}.coordinate.x`)}
                        value={
                          watch(`totePlacements.${index}.coordinate.x`) ?? 0
                        }
                      >
                        {possibleGridCoordinates.map((numOption) => (
                          <MenuItem key={numOption} value={numOption}>
                            {`${numOption}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </PortLabeledRow>
                  <PortLabeledRow
                    label={t("layout coordinate y", {
                      defaultValue: "Y"
                    })}
                  >
                    <FormControl fullWidth size="small" disabled={!isAdmin}>
                      <Select
                        id={ySelectId}
                        variant="outlined"
                        disabled={!isAdmin}
                        sx={settingsSelectSx}
                        SelectDisplayProps={{ "aria-label": "Y" }}
                        {...register(`totePlacements.${index}.coordinate.y`)}
                        value={
                          watch(`totePlacements.${index}.coordinate.y`) ?? 0
                        }
                      >
                        {possibleGridCoordinates.map((numOption) => (
                          <MenuItem key={numOption} value={numOption}>
                            {`${numOption}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </PortLabeledRow>
                  <input
                    type="hidden"
                    {...register(`totePlacements.${index}.totePosition`, {
                      valueAsNumber: true
                    })}
                  />
                </Stack>
                {isAdmin && (
                  <Box
                    id={`ws-ptl-card-${index}-toolbar`}
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      width: "100%",
                      minHeight: 32,
                      pt: 0.5
                    }}
                  >
                    <IconButton
                      size="small"
                      aria-label={t("remove put to light device")}
                      onClick={() => removeTotePlacement(index)}
                      sx={{ mb: -0.5, mr: -0.5 }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Paper>
            );
          })}
        </Box>
        {isAdmin && (
          <Stack
            id="ws-ptl-add-wrap"
            direction="column"
            spacing={0.5}
            alignItems="center"
            justifyContent="center"
            sx={{ flexShrink: 0, alignSelf: "stretch" }}
          >
            <SquareIconButton
              aria-label={t("add tote placement")}
              onClick={() => addTotePlacement(createNewTotePlacementTemplate())}
              size="medium"
              sx={ptlIconButtonSx}
            >
              <Plus20Px />
            </SquareIconButton>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
